/**
 * @fileoverview Country utilities for code/name conversions.
 * @module shared/utils/countryUtils
 */

import { useTranslation } from 'react-i18next'

/**
 * Converts a two-letter country code to its flag emoji.
 *
 * @param code - ISO 3166-1 alpha-2 country code (e.g., 'US', 'PL')
 * @returns Flag emoji or globe icon if code is invalid
 * @example
 * codeToFlag('US') // '🇺🇸'
 * codeToFlag('PL') // '🇵🇱'
 * codeToFlag('invalid') // '🌐'
 */
export function codeToFlag(code: string): string {
  if (!code || code.length !== 2) return '🌐'
  const base = 127397
  const upper = code.toUpperCase()
  return String.fromCodePoint(...[...upper].map(c => base + c.charCodeAt(0)))
}

/**
 * Hook for use in React components to get localized country names.
 * Returns a function that converts country codes to localized names.
 *
 * @returns Function to convert country codes to localized names
 * @example
 * const getCountryName = useCountryNames()
 * getCountryName('PL') // 'Polska' (in Polish) or 'Poland' (in English)
 */
export function useCountryNames(): (code: string) => string {
  const { t } = useTranslation('common')

  return (code: string): string => {
    const countryCode = code.toUpperCase()
    const translatedName = t(`countries.${countryCode}`, { defaultValue: '' })
    return translatedName || code
  }
}
